import { useMutation } from '@tanstack/react-query'
import { ExternalLink, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button, type ButtonProps } from '@alerte-immo/ui'
import { extractApiMessage } from '@/lib/apiError'
import { createBillingPortalSession } from '../api'

/**
 * Régularisation d'un plan `unpaid` : ouvre le portail de facturation Stripe
 * dans un nouvel onglet. Le règlement de la facture rétablit l'offre ; le plan
 * est relu au retour sur l'app (cf. AppLayout). L'onglet est ouvert pendant le
 * clic, avant l'appel API : un `window.open` après coup serait bloqué comme
 * pop-up.
 */
export function PayInvoiceButton(
  props: Omit<ButtonProps, 'asChild' | 'children' | 'disabled' | 'onClick'>,
) {
  const portal = useMutation<string, unknown, Window | null>({
    mutationFn: () => createBillingPortalSession(),
    // URL éphémère : utilisée tout de suite, jamais mise en cache.
    onSuccess: (url, tab) => {
      if (tab) tab.location.href = url
      else window.location.assign(url)
    },
    onError: (err, tab) => {
      tab?.close()
      toast.error(extractApiMessage(err, 'Portail de facturation indisponible. Réessayez plus tard.'))
    },
  })

  const open = () => {
    const tab = window.open('', '_blank')
    // Équivalent noopener : l'onglet du portail n'a pas la main sur l'app.
    if (tab) tab.opener = null
    portal.mutate(tab)
  }

  return (
    <Button {...props} disabled={portal.isPending} onClick={open}>
      {portal.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <ExternalLink className="h-4 w-4" />
      )}
      Régulariser ma facture
    </Button>
  )
}
